import type { LigneDetailParticipant } from '../types';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import { fmtInt, fmtHeures, fmtPct, fmtNb2, fmtDateFr } from '../utils/format';
import { AnalyticsReponse, Filtres, PUBLIC_COULEUR, LigneCapacite } from '../types';

// ── Barre de filtres : période (3 modes) + 5 multi-sélections composables. ────
type PMode = 'sem' | 'mois' | 'dates';
type Sel = Set<string> | null; // null = « tous » ; Set vide = « aucun »
type Tab = 'part' | 'esp' | 'mes' | 'mod' | 'det';

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const ANNEES = [2025, 2026];

function dernierJour(y: number, m: number): number {
  return new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
}
// Param d'une sélection : null (tous) ou tous cochés → omis ; sinon liste (vide = « aucun »).
function selParam(sel: Sel, total: number): string | null {
  if (sel === null) return null;
  if (total > 0 && sel.size === total) return null;
  return [...sel].join(',');
}
function badgeNiveau(n: LigneCapacite['niveauUsage']): { cls: string; txt: string } {
  return n === 'Usage fort' ? { cls: 'b-fort', txt: 'Fort' }
    : n === 'Usage modéré' ? { cls: 'b-mod', txt: 'Modéré' }
    : { cls: 'b-faible', txt: 'Faible' };
}
function bandeOccupation(pct: number): string {
  return pct >= 40 ? 'var(--good)' : pct >= 25 ? 'var(--warn)' : 'var(--crit)';
}

/* ── Multi-sélection (checkboxes, Tout/Aucun) ─────────────────────────────── */
function MultiSelect({ label, options, sel, onChange }: {
  label: string;
  options: { value: string; label: string }[];
  sel: Sel;
  onChange: (s: Sel) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const total = options.length;
  const count = sel === null ? total : sel.size;
  const isAll = sel === null || (total > 0 && sel.size === total);
  const checked = (v: string) => (sel === null ? true : sel.has(v));
  const toggle = (v: string) => {
    const base = sel === null ? new Set(options.map((o) => o.value)) : new Set(sel);
    if (base.has(v)) base.delete(v); else base.add(v);
    onChange(base.size === total ? null : base);
  };

  return (
    <div className="an-ms" ref={ref}>
      <button className="an-ms-btn" type="button" onClick={() => setOpen((o) => !o)}>
        {label} : {isAll ? <>Tous <span className="an-cnt">{total}</span></> : count === 0 ? 'Aucun' : <span className="an-cnt">{count}/{total}</span>}
        <svg viewBox="0 0 10 6" width="9" height="6" aria-hidden><path d="M1 1l4 4 4-4" stroke="currentColor" fill="none" strokeWidth="1.5" /></svg>
      </button>
      {open && (
        <div className="an-ms-pop">
          <div className="an-ms-tools">
            <button type="button" onClick={() => onChange(null)}>Tout</button>
            <button type="button" onClick={() => onChange(new Set())}>Aucun</button>
          </div>
          {options.map((o) => (
            <label key={o.value} className="an-ms-row">
              <input type="checkbox" checked={checked(o.value)} onChange={() => toggle(o.value)} />
              <span>{o.label}</span>
            </label>
          ))}
          {total === 0 && <div className="an-empty">—</div>}
        </div>
      )}
    </div>
  );
}

/* ── Barres horizontales ──────────────────────────────────────────────────── */
function Bar({ label, sub, widthPct, valeur, couleur }: { label: string; sub?: ReactNode; widthPct: number; valeur: string; couleur?: string }) {
  return (
    <div className="an-bar">
      <span className="an-bl" title={label}>{label}{sub && <span className="an-bl-sub"> {sub}</span>}</span>
      <div className="an-track"><div className="an-fill" style={{ width: `${Math.max(2, Math.min(100, widthPct))}%`, background: couleur ?? 'var(--seq)' }} /></div>
      <span className="an-bv tnum">{valeur}</span>
    </div>
  );
}

/* ── Graphe d'aire (évolution) ────────────────────────────────────────────── */
function AreaChart({ points }: { points: { label: string; heures: number }[] }) {
  if (points.length === 0) return <div className="an-empty">Aucune donnée sur cette période.</div>;
  if (points.length === 1) {
    return (
      <div style={{ padding: '26px 4px' }}>
        <div className="tnum" style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-.02em' }}>{fmtHeures(points[0].heures)}</div>
        <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 4 }}>{points[0].label} · une seule période</div>
      </div>
    );
  }
  const W = 440, H = 170, pl = 34, pr = 12, pt = 14, pb = 26;
  const xs = (i: number) => pl + ((W - pl - pr) * i) / (points.length - 1);
  const vals = points.map((d) => d.heures);
  const mx = Math.max(...vals) * 1.1 || 1;
  const mn = Math.min(...vals) * 0.85;
  const ys = (v: number) => pt + (H - pt - pb) * (1 - (v - mn) / ((mx - mn) || 1));
  const pts = points.map((d, i) => [xs(i), ys(d.heures)] as const);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const area = `M${pl} ${H - pb} ${pts.map((p) => `L${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')} L${W - pr} ${H - pb} Z`;
  const grid = [0, 1, 2].map((g) => {
    const v = mn + ((mx - mn) * g) / 2;
    const y = ys(v);
    return <g key={g}><line x1={pl} x2={W - pr} y1={y} y2={y} stroke="var(--hair)" /><text x={4} y={y + 3}>{v >= 1000 ? `${Math.round(v / 1000)}k` : Math.round(v)}</text></g>;
  });
  const step = Math.ceil(points.length / 8);
  const anch = (i: number) => (i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle');
  return (
    <svg className="an-area" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Évolution des heures réalisées">
      <defs><linearGradient id="an-ag" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="var(--seq)" stopOpacity=".22" /><stop offset="1" stopColor="var(--seq)" stopOpacity="0" /></linearGradient></defs>
      {grid}
      <path d={area} fill="url(#an-ag)" />
      <path d={line} fill="none" stroke="var(--seq)" strokeWidth={2} />
      {pts.map((p, i) => (<circle key={i} cx={p[0].toFixed(1)} cy={p[1].toFixed(1)} r={i === pts.length - 1 ? 4 : 2.6} fill={i === pts.length - 1 ? 'var(--accent)' : 'var(--seq)'}><title>{points[i].label} : {fmtHeures(points[i].heures)}</title></circle>))}
      {points.map((d, i) => (i % step === 0 || i === points.length - 1) ? <text key={`t${i}`} x={xs(i).toFixed(1)} y={H - 8} textAnchor={anch(i)}>{d.label}</text> : null)}
    </svg>
  );
}

function Kpi({ label, value, meta }: { label: string; value: ReactNode; meta?: string }) {
  return (
    <div className="kpi-card">
      <span className="kpi-card__label">{label}</span>
      <span className="kpi-card__value">{value}</span>
      {meta && <span className="kpi-card__meta">{meta}</span>}
    </div>
  );
}

/* ══ Page ═══════════════════════════════════════════════════════════════════ */
export function AnalysesPage({ section = 'part' }: { section?: Tab }) {
  const tab = section;

  // Période — défaut : 1er semestre 2026 (couvre l'étendue réelle des données).
  const [pmode, setPmode] = useState<PMode>('sem');
  const [pYear, setPYear] = useState(2026);
  const [pSem, setPSem] = useState<1 | 2>(1);
  const [pMonth, setPMonth] = useState(0);
  const [dFrom, setDFrom] = useState('');
  const [dTo, setDTo] = useState('');

  // Multi-sélections — défaut : toutes ouvertes (null).
  const [selPub, setSelPub] = useState<Sel>(null);
  const [selCat, setSelCat] = useState<Sel>(null);
  const [selPole, setSelPole] = useState<Sel>(null);
  const [selMes, setSelMes] = useState<Sel>(null);
  const [selLieu, setSelLieu] = useState<Sel>(null);
  const [selMand, setSelMand] = useState<Sel>(null);      // drilldown mandants (AI / RI-CSR)
  const [detail, setDetail] = useState<LigneDetailParticipant[] | null>(null);
  const [mesRecherche, setMesRecherche] = useState('');
  const [mesPublic, setMesPublic] = useState<string | null>(null);
  const [rechercheDet, setRechercheDet] = useState('');
  const [triDet, setTriDet] = useState<'nom' | 'heures' | 'seancesPresentes' | 'absences' | 'tauxAbsenteisme' | 'nbFamilles'>('heures');
  const [descDet, setDescDet] = useState(true);
  const [socioPro, setSocioPro] = useState(false);        // préréglage socio-pro (décision opérateur)

  const [filtres, setFiltres] = useState<Filtres | null>(null);
  const [data, setData] = useState<AnalyticsReponse | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => { api.get<Filtres>('/ressources/filtres').then(setFiltres).catch(() => {}); }, []);

  const range = useMemo(() => {
    if (pmode === 'sem') return pSem === 1 ? { from: `${pYear}-01-01`, to: `${pYear}-06-30` } : { from: `${pYear}-07-01`, to: `${pYear}-12-31` };
    if (pmode === 'mois') { const mm = String(pMonth + 1).padStart(2, '0'); return { from: `${pYear}-${mm}-01`, to: `${pYear}-${mm}-${String(dernierJour(pYear, pMonth)).padStart(2, '0')}` }; }
    return { from: dFrom, to: dTo };
  }, [pmode, pYear, pSem, pMonth, dFrom, dTo]);

  const optPub = useMemo(() => filtres?.publics.map((p) => ({ value: p.value, label: p.label })) ?? [], [filtres]);
  const optCat = useMemo(() => filtres?.categories.map((c) => ({ value: String(c.id), label: c.nom })) ?? [], [filtres]);
  const optPole = useMemo(() => filtres?.poles.map((p) => ({ value: String(p.id), label: p.nom })) ?? [], [filtres]);
  const optMes = useMemo(() => filtres?.mesures.map((m) => ({ value: String(m.id), label: m.nom })) ?? [], [filtres]);
  const optLieu = useMemo(() => filtres?.lieux.map((l) => ({ value: String(l.id), label: l.nom })) ?? [], [filtres]);
  const optMand = useMemo(
    () => filtres?.mandants.map((m) => ({ value: String(m.id), label: `${m.groupe} · ${m.nom}` })) ?? [],
    [filtres]
  );

  const qs = useMemo(() => {
    const q = new URLSearchParams();
    if (range.from) q.set('from', range.from);
    if (range.to) q.set('to', range.to);
    const add = (key: string, sel: Sel, total: number) => { const p = selParam(sel, total); if (p !== null) q.set(key, p); };
    add('publics', selPub, optPub.length);
    add('categories', selCat, optCat.length);
    add('poles', selPole, optPole.length);
    add('mesures', selMes, optMes.length);
    add('lieux', selLieu, optLieu.length);
    add('mandants', selMand, optMand.length);
    if (socioPro) q.set('sociopro', '1');
    return q.toString();
  }, [range, selPub, selCat, selPole, selMes, selLieu, selMand, socioPro, optPub.length, optCat.length, optPole.length, optMes.length, optLieu.length, optMand.length]);

  useEffect(() => {
    if (!range.from || !range.to) { setChargement(false); return; }
    setChargement(true);
    api.get<AnalyticsReponse>(`/ressources/analytics?${qs}`)
      .then((d) => { setData(d); setErreur(null); })
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [qs, range.from, range.to]);

  const reinitialiser = () => {
    setPmode('sem'); setPYear(2026); setPSem(1); setPMonth(0); setDFrom(''); setDTo('');
    setSelPub(null); setSelCat(null); setSelPole(null); setSelMes(null); setSelLieu(null);
    setSelMand(null); setSocioPro(false);
  };
  const setMode = (m: PMode) => {
    if (m === 'dates' && !dFrom) { setDFrom(range.from); setDTo(range.to); }
    setPmode(m);
  };

  const k = data?.kpis;
  useEffect(() => {
    if (tab !== 'det') return;
    setDetail(null);
    fetch(`/api/ressources/participants-detail?${qs}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((r) => setDetail(r.lignes ?? []))
      .catch(() => setDetail([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, qs]);

  const filtresActifs = [selPub, selCat, selPole, selMes, selLieu, selMand].some((s) => s !== null) || socioPro;

  return (
    <div className="an-page">
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">Analyses des ressources</h1>
          {data && <span className="stat-pill">base {data.nbSemainesBase} sem.</span>}
        </div>
      </div>

      {/* ── Barre de filtres globale ── */}
      <div className="an-filters">
        <div className="an-fgroup">
          <label>Période</label>
          <div className="an-per">
            <div className="an-seg" role="group" aria-label="Granularité de la période">
              <button type="button" aria-pressed={pmode === 'sem'} onClick={() => setMode('sem')}>Semestre</button>
              <button type="button" aria-pressed={pmode === 'mois'} onClick={() => setMode('mois')}>Mois</button>
              <button type="button" aria-pressed={pmode === 'dates'} onClick={() => setMode('dates')}>Dates</button>
            </div>
            {pmode === 'sem' && (
              <>
                <select className="an-sel" value={pYear} onChange={(e) => setPYear(Number(e.target.value))}>{ANNEES.map((y) => <option key={y} value={y}>{y}</option>)}</select>
                <select className="an-sel" value={pSem} onChange={(e) => setPSem(Number(e.target.value) as 1 | 2)}><option value={1}>1er semestre</option><option value={2}>2e semestre</option></select>
              </>
            )}
            {pmode === 'mois' && (
              <>
                <select className="an-sel" value={pYear} onChange={(e) => setPYear(Number(e.target.value))}>{ANNEES.map((y) => <option key={y} value={y}>{y}</option>)}</select>
                <select className="an-sel" value={pMonth} onChange={(e) => setPMonth(Number(e.target.value))}>{MOIS.map((m, i) => <option key={i} value={i}>{m}</option>)}</select>
              </>
            )}
            {pmode === 'dates' && (
              <span className="an-dates">du <input type="date" className="date-input" value={dFrom} onChange={(e) => setDFrom(e.target.value)} /> au <input type="date" className="date-input" value={dTo} onChange={(e) => setDTo(e.target.value)} /></span>
            )}
          </div>
        </div>
        <div className="an-fgroup"><label>Public</label><MultiSelect label="Public" options={optPub} sel={selPub} onChange={setSelPub} /></div>
        <div className="an-fgroup"><label>Mandant</label><MultiSelect label="Mandant" options={optMand} sel={selMand} onChange={setSelMand} /></div>
        <div className="an-fgroup">
          <label>Préréglage</label>
          <button type="button" className={`chip${socioPro ? ' active' : ''}`}
                  title="Espaces Théorique & Pratique de travail + Placements + Suivi Transition / Vers l'emploi"
                  onClick={() => setSocioPro((v) => !v)}>
            Socio-pro
          </button>
        </div>
        <div className="an-fgroup"><label>Catégorie</label><MultiSelect label="Catégorie" options={optCat} sel={selCat} onChange={setSelCat} /></div>
        <div className="an-fgroup"><label>Pôle</label><MultiSelect label="Pôle" options={optPole} sel={selPole} onChange={setSelPole} /></div>
        <div className="an-fgroup"><label>Mesure</label><MultiSelect label="Mesure" options={optMes} sel={selMes} onChange={setSelMes} /></div>
        <div className="an-fgroup"><label>Site</label><MultiSelect label="Site" options={optLieu} sel={selLieu} onChange={setSelLieu} /></div>
        <div className="an-fgroup"><label>&nbsp;</label><button className="chip" type="button" onClick={reinitialiser}>Réinitialiser</button></div>
      </div>

      {/* ── Onglets ── */}

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      {data && (
        <div className="an-slice">
          <b>{fmtDateFr(range.from)} → {fmtDateFr(range.to)}</b> · {data.nbSemainesBase} sem. actives —
          {' '}{filtresActifs ? 'filtres appliqués' : 'tous filtres ouverts'} · <b>{fmtInt(k!.presences)}</b> présences · <b>{fmtHeures(k!.heuresRealisees)}</b>
        </div>
      )}

      {chargement && !data ? (
        <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>
      ) : !data ? null : (
        <>
          {/* ══ ESPACE 1 ══ */}
          {tab === 'part' && (
            <>
              <div className="kpi-row mb-16">
                <Kpi label="Participants distincts" value={fmtInt(k!.participantsDistinct)} meta="sur la sélection" />
                <Kpi label="Présences effectives" value={fmtInt(k!.presences)} meta="séances suivies" />
                <Kpi label="Taux d'absentéisme" value={fmtPct(k!.tauxAbsenteisme)} meta="excusé·es + absent·es — dispensé·es hors calcul" />
                <Kpi label="Heures réalisées" value={fmtHeures(k!.heuresRealisees)} meta="temps effectif" />
              </div>
              <div className="an-grid">
                <div className="an-card an-col-5">
                  <header className="an-card-h"><h3>Répartition de l'effort</h3><span className="an-sub">heures · par {data.effort.mode === 'pole' ? 'pôle' : 'catégorie'}</span></header>
                  {data.effort.items.length ? (
                    <div className="an-bars">
                      {(() => { const mx = Math.max(1, ...data.effort.items.map((i) => i.heures)); return data.effort.items.map((it) => (<Bar key={it.label} label={it.label} widthPct={(it.heures / mx) * 100} valeur={fmtHeures(it.heures)} />)); })()}
                    </div>
                  ) : <div className="an-empty">Aucune donnée pour ces filtres.</div>}
                  {data.effort.mode === 'categorie' && <p className="an-note">Bascule <b>par pôle</b> quand une seule catégorie est sélectionnée.</p>}
                </div>
                <div className="an-card an-col-7">
                  <header className="an-card-h"><h3>Capacité hebdomadaire</h3><span className="an-sub">{data.capacite.length} prestations · par semaine active</span></header>
                  <div className="table-scroll an-cap-scroll">
                    <table className="table table-compact">
                      <thead><tr>
                        <th>Prestation · pôle</th>
                        <th className="cell-num">Sem.</th><th className="cell-num">Part./sem</th><th className="cell-num">Pic</th>
                        <th className="cell-num">Présences</th><th className="cell-num">Heures</th><th>Usage</th>
                      </tr></thead>
                      <tbody>
                        {data.capacite.map((l) => { const b = badgeNiveau(l.niveauUsage); return (
                          <tr key={l.prestationId}>
                            <td className="cell-prestation"><span className="nom-prestation">{l.prestationNom}</span><span className="text-xs text-ink-3 block">{l.categorieNom} · {l.poleNom}</span></td>
                            <td className="cell-num">{l.nbSemaines}</td>
                            <td className="cell-num">{fmtNb2(l.participantsActifs)}</td>
                            <td className="cell-num">{l.picHebdo}</td>
                            <td className="cell-num">{fmtInt(l.totalPresences)}</td>
                            <td className="cell-num">{fmtHeures(l.totalHeures)}</td>
                            <td><span className={`an-badge ${b.cls}`}>{b.txt}</span></td>
                          </tr>
                        ); })}
                        {data.capacite.length === 0 && <tr><td colSpan={7} className="text-center text-ink-3 p-16">Aucune prestation pour ces filtres.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <p className="an-note"><b>Niveau d'usage</b> = participants distincts / semaine active. <span className="an-badge b-fort">Fort ≥ 5</span> <span className="an-badge b-mod">Modéré 1–5</span> <span className="an-badge b-faible">Faible &lt; 1</span></p>
                </div>
              </div>
            </>
          )}

          {/* ══ ESPACE 2 ══ */}
          {tab === 'esp' && (() => {
            const e = data.espaces;
            const occ = e.occupation.filter((o) => o.configuree && o.tauxOccupationSalles != null && o.heuresRealisees > 0).map((o) => ({ ...o, pct: (o.tauxOccupationSalles as number) * 100 })).sort((a, b) => b.pct - a.pct);
            const maxPerdu = Math.max(1, ...e.planifieVsRealise.map((p) => p.pctPerdu * 100));
            return (
              <>
                <div className="kpi-row mb-16">
                  <Kpi label="Heures réalisées" value={fmtHeures(k!.heuresRealisees)} meta={`sur ${fmtHeures(k!.heuresPlanifiees)} planifiées`} />
                  <Kpi label="Taux d'occupation moyen" value={e.tauxOccupationMoyen != null ? fmtPct(e.tauxOccupationMoyen) : '—'} meta="heures-salle / capacité" />
                  <Kpi label="Heures perdues (no-show)" value={fmtHeures(k!.heuresPerdues)} meta={k!.heuresPlanifiees ? `${fmtPct(k!.heuresPerdues / k!.heuresPlanifiees)} du planifié` : '—'} />
                  <Kpi label="Sites actifs" value={fmtInt(e.sitesActifs)} meta={`sur ${e.sitesTotal}`} />
                </div>
                <div className="an-grid">
                  <div className="an-card an-col-6">
                    <header className="an-card-h"><h3>Occupation des salles par site</h3><span className="an-sub">heures-salle / capacité</span></header>
                    {occ.length ? <div className="an-bars">{occ.map((o) => (<Bar key={o.lieuId} label={o.lieuNom ?? `Lieu #${o.lieuId}`} widthPct={o.pct / 50 * 100} valeur={fmtPct(o.tauxOccupationSalles!)} couleur={bandeOccupation(o.pct)} />))}</div> : <div className="an-empty">Aucune donnée (sites non configurés).</div>}
                    <div className="an-legend"><span className="an-li"><span className="an-sw" style={{ background: 'var(--good)' }} />≥ 40 %</span><span className="an-li"><span className="an-sw" style={{ background: 'var(--warn)' }} />25–40 %</span><span className="an-li"><span className="an-sw" style={{ background: 'var(--crit)' }} />&lt; 25 %</span></div>
                  </div>
                  <div className="an-card an-col-6">
                    <header className="an-card-h"><h3>Planifié vs réalisé</h3><span className="an-sub">heures perdues par absence</span></header>
                    {e.planifieVsRealise.length ? <div className="an-bars">{e.planifieVsRealise.map((p) => (<Bar key={p.lieuId} label={p.lieuNom ?? `Lieu #${p.lieuId}`} widthPct={(p.pctPerdu * 100) / maxPerdu * 100} valeur={fmtPct(p.pctPerdu)} couleur="var(--s-ctt)" />))}</div> : <div className="an-empty">—</div>}
                    <p className="an-note">Part des <b>heures-séance non réalisées</b> (planifié non honoré) par site.</p>
                  </div>
                  <div className="an-card an-col-7">
                    <header className="an-card-h"><h3>Mixité des publics par site</h3><span className="an-sub">heures réalisées</span></header>
                    <div className="an-bars">
                      {e.mixite.map((m) => { const t = m.total || 1; return (
                        <div className="an-bar an-bar--stack" key={m.lieuId}>
                          <span className="an-bl">{m.lieuNom ?? `Lieu #${m.lieuId}`}</span>
                          <div className="an-stack">{['AI', 'RI-CCC', 'RI-CTT', 'SISP', 'Autres'].map((p) => { const v = m.parPublic[p] || 0; return v > 0 ? <span key={p} style={{ width: `${(v / t) * 100}%`, background: PUBLIC_COULEUR[p] }} title={`${p} : ${fmtHeures(v)}`} /> : null; })}</div>
                          <span className="an-bv tnum">{fmtHeures(m.total)}</span>
                        </div>
                      ); })}
                      {e.mixite.length === 0 && <div className="an-empty">Aucune donnée.</div>}
                    </div>
                    <div className="an-legend" style={{ marginTop: 12 }}>{['AI', 'RI-CCC', 'RI-CTT', 'SISP', 'Autres'].map((p) => <span className="an-li" key={p}><span className="an-sw" style={{ background: PUBLIC_COULEUR[p] }} />{p}</span>)}</div>
                  </div>
                  <div className="an-card an-col-5">
                    <header className="an-card-h"><h3>Évolution</h3><span className="an-sub">heures réalisées · par {e.evolution.mode === 'mois' ? 'mois' : 'semaine'}</span></header>
                    <AreaChart points={e.evolution.points} />
                  </div>
                  {e.placement.length > 0 && (
                    <div className="an-card an-col-7">
                      <header className="an-card-h">
                        <h3>Stages (Placement) — capacité côté personnel</h3>
                        <span className="an-sub">heures d'encadrement · les stages n'occupent pas de salle</span>
                      </header>
                      <div className="table-scroll">
                        <table className="table table-compact">
                          <thead><tr><th>Intervenant·e</th><th className="cell-num">Séances</th><th className="cell-num">Heures encadrées</th></tr></thead>
                          <tbody>
                            {e.placement.map((p) => (
                              <tr key={p.intervenantNom}>
                                <td>{p.intervenantNom}</td>
                                <td className="cell-num">{fmtInt(p.seances)}</td>
                                <td className="cell-num">{fmtHeures(p.heures)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ── Impact de la fin du RI-CTT (indicateur stratégique) ── */}
                  <div className="an-card an-col-12">
                    <header className="an-card-h">
                      <h3>Impact de la fin du RI-CTT (31.12.2026)</h3>
                      <span className="an-sub">combien d'heures chaque site perd quand la convention s'arrête — et ce qu'il reste</span>
                    </header>
                    <div className="table-scroll">
                      <table className="table table-compact">
                        <thead><tr>
                          <th>Site</th><th className="cell-num">Heures</th><th className="cell-num">Part.</th>
                          <th className="cell-num">Absent.</th><th className="cell-num">Heures RI-CTT</th>
                          <th className="cell-num">Part CTT du site</th><th className="cell-num">Restantes</th>
                          <th className="cell-num">Part de tout le CTT</th><th>Exposition</th>
                        </tr></thead>
                        <tbody>
                          {data.impactCtt.map((l) => (
                            <tr key={l.lieuNom}>
                              <td>{l.lieuNom}</td>
                              <td className="cell-num tnum">{fmtHeures(l.heures)}</td>
                              <td className="cell-num">{fmtInt(l.participants)}</td>
                              <td className="cell-num">{l.tauxAbsenteisme != null ? fmtPct(l.tauxAbsenteisme) : '—'}</td>
                              <td className="cell-num tnum">{fmtHeures(l.heuresCtt)}</td>
                              <td className="cell-num">{fmtPct(l.partSite)}</td>
                              <td className="cell-num tnum">{fmtHeures(l.restantes)}</td>
                              <td className="cell-num">{fmtPct(l.partDesCtt)}</td>
                              <td><span className={`stat-pill${l.exposition === 'Élevée' ? ' pill-err' : l.exposition === 'Moyenne' ? ' pill-warn' : ''}`}>{l.exposition}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="an-note">
                      Les sites portés par le RI-CCC et l'AI sont peu affectés — l'enjeu est d'anticiper le
                      remplacement des heures CTT là où l'exposition est élevée. Le tableau porte aussi
                      l'utilisation par site (participants distincts, absentéisme hors dispensé·es).
                      <br />⚠ « Non renseigné » regroupe l'activité SANS salle rattachée dans la base
                      canonique — surtout les stages (Placement) et certains espaces. L'analyse de
                      référence attribuait ces heures à leur site via le texte libre du système
                      historique (l'essentiel du CTT « non renseigné » = stages basés à Beaulieu) ;
                      cette attribution sera restaurée quand la migration portera le lieu des séances.
                    </p>
                  </div>

                  {/* ── Vocation des sites : matrice site × pôle ── */}
                  <div className="an-card an-col-12">
                    <header className="an-card-h">
                      <h3>Vocation des sites</h3>
                      <span className="an-sub">heures réalisées par pôle — teinte foncée = concentration</span>
                    </header>
                    {(() => {
                      const lieux = [...new Set(data.vocation.map((v) => v.lieuNom))];
                      const poles = [...new Set(data.vocation.map((v) => v.poleNom))]
                        .sort((a, b) => data.vocation.filter((v) => v.poleNom === b).reduce((s, v) => s + v.heures, 0)
                                      - data.vocation.filter((v) => v.poleNom === a).reduce((s, v) => s + v.heures, 0));
                      const h = new Map(data.vocation.map((v) => [`${v.lieuNom}|${v.poleNom}`, v.heures]));
                      const max = Math.max(1, ...data.vocation.map((v) => v.heures));
                      return (
                        <div className="table-scroll">
                          <table className="table table-compact">
                            <thead><tr><th>Site</th>{poles.map((p) => <th key={p} className="cell-num">{p}</th>)}</tr></thead>
                            <tbody>
                              {lieux.map((lieu) => (
                                <tr key={lieu}>
                                  <td>{lieu}</td>
                                  {poles.map((pole) => {
                                    const v = h.get(`${lieu}|${pole}`);
                                    return (
                                      <td key={pole} className="cell-num tnum"
                                          style={v ? { background: `color-mix(in srgb, var(--s-ai, #4a7dbd) ${Math.round(8 + 55 * (v / max))}%, transparent)` } : undefined}>
                                        {v ? fmtHeures(v) : '—'}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                    <p className="an-note">
                      Lecture en ligne : à quoi sert chaque site. Lecture en colonne : où se pratique chaque
                      pôle d'activité. Complète l'occupation des salles par la nature de l'activité.
                    </p>
                  </div>
                </div>
              </>
            );
          })()}

          {/* ══ DÉTAIL PARTICIPANTS ══ */}
          {tab === 'det' && (() => {
            const q = rechercheDet.trim().toLowerCase();
            let lignes = (detail ?? []).filter((l) => !q || l.nom.toLowerCase().includes(q)
              || l.mesures.some((m) => m.toLowerCase().includes(q))
              || l.familles.some((f2) => f2.toLowerCase().includes(q)));
            const dir = descDet ? -1 : 1;
            lignes = [...lignes].sort((x, y) => {
              const vx = x[triDet], vy = y[triDet];
              if (vx == null) return 1;
              if (vy == null) return -1;
              return typeof vx === 'string' ? dir * vx.localeCompare(vy as string, 'fr') : dir * ((vx as number) - (vy as number));
            });
            const Th2 = ({ c, children, num = true }: { c: typeof triDet; children: React.ReactNode; num?: boolean }) => (
              <th className={num ? 'cell-num' : undefined} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  onClick={() => { if (triDet === c) setDescDet(!descDet); else { setTriDet(c); setDescDet(c !== 'nom'); } }}>
                {children}{triDet === c ? (descDet ? ' ↓' : ' ↑') : ''}
              </th>
            );
            const exporter = () => {
              const entetes = ['participant', 'publics', 'mesures', 'familles', 'nb_familles', 'seances_presentes', 'heures', 'absences', 'dispenses', 'taux_absenteisme'];
              const corps = lignes.map((l) => [
                l.nom, l.publics.join(' / '), l.mesures.join(' / '), l.familles.join(' / '),
                l.nbFamilles, l.seancesPresentes, l.heures, l.absences, l.dispenses,
                l.tauxAbsenteisme != null ? Math.round(l.tauxAbsenteisme * 1000) / 10 + '%' : '',
              ].map((v) => { const t = String(v ?? ''); return /[";\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; }).join(';')).join('\n');
              const blob = new Blob(['\ufeff' + entetes.join(';') + '\n' + corps], { type: 'text/csv;charset=utf-8' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `participants_${range.from}_${range.to}.csv`;
              a.click(); URL.revokeObjectURL(a.href);
            };
            return (
              <div className="an-grid">
                <div className="an-card an-col-12">
                  <header className="an-card-h">
                    <h3>Détail par participant·e</h3>
                    <span className="an-sub">{lignes.length} participant·es du périmètre filtré · accès restreint de l'app assumé</span>
                  </header>
                  <div className="flex items-center" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <input className="an-sel" style={{ flex: '0 1 280px' }} placeholder="Nom, mesure ou pôle…"
                           value={rechercheDet} onChange={(ev) => setRechercheDet(ev.target.value)} />
                    <button type="button" className="an-sel" style={{ cursor: 'pointer' }} onClick={exporter}>
                      Exporter participants (CSV)
                    </button>
                    <a className="an-sel" style={{ cursor: 'pointer', textDecoration: 'none' }}
                       href={`/api/ressources/export-brut?${qs}`}>
                      Données brutes (CSV)
                    </a>
                  </div>
                  {detail == null && <div className="an-empty">Chargement…</div>}
                  {detail != null && (
                    <div className="table-scroll">
                      <table className="table table-compact">
                        <thead><tr>
                          <Th2 c="nom" num={false}>Participant·e</Th2>
                          <th>Public(s)</th>
                          <th>Mesures</th>
                          <th>Pôles fréquentés</th>
                          <Th2 c="nbFamilles">Nb pôles</Th2>
                          <Th2 c="seancesPresentes">Séances prés.</Th2>
                          <Th2 c="heures">Heures</Th2>
                          <Th2 c="absences">Abs.</Th2>
                          <th className="cell-num">Disp.</th>
                          <Th2 c="tauxAbsenteisme">Taux abs.</Th2>
                        </tr></thead>
                        <tbody>
                          {lignes.map((l) => (
                            <tr key={l.participantId}>
                              <td style={{ whiteSpace: 'nowrap' }}>{l.nom}</td>
                              <td>{l.publics.join(' · ')}</td>
                              <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={l.mesures.join(' · ')}>{l.mesures.join(' · ')}</td>
                              <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={l.familles.join(' · ')}>{l.familles.join(' · ')}</td>
                              <td className="cell-num">{l.nbFamilles}</td>
                              <td className="cell-num">{fmtInt(l.seancesPresentes)}</td>
                              <td className="cell-num tnum">{fmtHeures(l.heures)}</td>
                              <td className="cell-num">{fmtInt(l.absences)}</td>
                              <td className="cell-num">{l.dispenses > 0 ? fmtInt(l.dispenses) : '—'}</td>
                              <td className="cell-num">{l.tauxAbsenteisme != null ? fmtPct(l.tauxAbsenteisme) : '—'}</td>
                            </tr>
                          ))}
                          {lignes.length === 0 && <tr><td colSpan={10} className="text-center text-ink-3 p-16">Aucun·e participant·e sur ce périmètre.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="an-note">
                    Une ligne par participant·e du périmètre (filtres globaux appliqués : publics, mesures,
                    pôles, lieux, période). Heures = présences validées ; dispensé·es hors du taux
                    d'absentéisme. L'export « Données brutes » reprend le détail séance par séance.
                  </p>
                </div>
              </div>
            );
          })()}

          {/* ══ CONSOMMATION DES MESURES ══ */}
          {tab === 'mes' && (() => {
            const maxRi = Math.max(1, ...data.mesures.ri.map((r) => r.heures));
            const couleurRi: Record<string, string> = { ccc: 'var(--s-ccc)', ctt: 'var(--s-ctt)', sisp: 'var(--s-sisp, #7c5cff)', evam: 'var(--s-autre)', autre: 'var(--s-autre)' };
            // évolution mensuelle par famille : mois en lignes, segments par public
            const moisListe = [...new Set(data.famillesMois.map((x) => x.mois))].sort();
            const hFM = new Map(data.famillesMois.map((x) => [`${x.mois}|${x.publicType}`, x.heures]));
            const maxMois = Math.max(1, ...moisListe.map((m) => data.famillesMois.filter((x) => x.mois === m).reduce((sm, x) => sm + x.heures, 0)));
            const lignes = mesRecherche.trim()
              ? data.mesuresToutes.filter((l) => (l.nom + ' ' + (l.mandantNom ?? '') + ' ' + l.publicType).toLowerCase().includes(mesRecherche.trim().toLowerCase()))
              : data.mesuresToutes;
            const visibles = mesPublic ? lignes.filter((l) => l.publicType === mesPublic) : lignes;
            return (
              <div className="an-grid">
                <div className="an-card an-col-7">
                  <header className="an-card-h"><h3>Rythme mensuel par famille de mandants</h3><span className="an-sub">heures réalisées · un mois par ligne</span></header>
                  <div className="an-bars">
                    {moisListe.map((m) => {
                      const tot = data.famillesMois.filter((x) => x.mois === m).reduce((sm, x) => sm + x.heures, 0);
                      return (
                        <div className="an-bar an-bar--stack" key={m}>
                          <span className="an-bl">{m}</span>
                          <div className="an-stack" style={{ width: `${(tot / maxMois) * 100}%` }}>
                            {['AI', 'RI-CCC', 'RI-CTT', 'SISP', 'Autres'].map((pub) => {
                              const v = hFM.get(`${m}|${pub}`) ?? 0;
                              return v > 0 ? <span key={pub} style={{ width: `${(v / tot) * 100}%`, background: PUBLIC_COULEUR[pub] }} title={`${pub} : ${fmtHeures(v)}`} /> : null;
                            })}
                          </div>
                          <span className="an-bv tnum">{fmtHeures(tot)}</span>
                        </div>
                      );
                    })}
                    {moisListe.length === 0 && <div className="an-empty">Aucune donnée.</div>}
                  </div>
                  <div className="an-legend" style={{ marginTop: 12 }}>
                    {['AI', 'RI-CCC', 'RI-CTT', 'SISP', 'Autres'].map((pub) => (
                      <span className="an-li" key={pub}><span className="an-sw" style={{ background: PUBLIC_COULEUR[pub] }} />{pub}</span>
                    ))}
                  </div>
                  <p className="an-note">C'est ici que la décrue du RI-CTT (convention terminée au 31.12.2026) se lit
                    mois par mois — voir aussi « Impact de la fin du RI-CTT » dans Espaces &amp; occupation.</p>
                </div>
                <div className="an-card an-col-5">
                  <header className="an-card-h"><h3>Mesures RI — CCC vs CTT</h3><span className="an-sub">heures réalisées</span></header>
                  {data.mesures.ri.length ? <div className="an-bars">{data.mesures.ri.map((r) => (<Bar key={r.cle} label={r.label} widthPct={(r.heures / maxRi) * 100} valeur={fmtHeures(r.heures)} couleur={couleurRi[r.cle]} />))}</div> : <div className="an-empty">Aucune mesure RI dans la sélection.</div>}
                  <p className="an-note"><b>CCC</b> et <b>CTT</b> sont deux mesures distinctes du mandant CSR (RI) (CTT = contrat de travail temporaire). EVAM = mesure d'un autre mandant.</p>
                </div>
                <div className="an-card an-col-12">
                  <header className="an-card-h">
                    <h3>Toutes les mesures consommées</h3>
                    <span className="an-sub">{visibles.length} mesures · toutes familles (AI, RI, SISP, EVAM, autres)</span>
                  </header>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input className="an-sel" style={{ flex: '0 1 260px' }} placeholder="Mesure ou mandant…"
                           value={mesRecherche} onChange={(ev) => setMesRecherche(ev.target.value)} />
                    <span className={`chip${mesPublic == null ? ' active' : ''}`} onClick={() => setMesPublic(null)}>Toutes</span>
                    {['AI', 'RI-CCC', 'RI-CTT', 'SISP', 'Autres'].map((pub) => (
                      <span key={pub} className={`chip${mesPublic === pub ? ' active' : ''}`}
                            onClick={() => setMesPublic(mesPublic === pub ? null : pub)}>{pub}</span>
                    ))}
                  </div>
                  <div className="table-scroll">
                    <table className="table table-compact">
                      <thead><tr>
                        <th>Mesure</th><th>Public</th><th>Mandant</th>
                        <th className="cell-num">Part.</th><th className="cell-num">Présences</th>
                        <th className="cell-num">Absent.</th><th className="cell-num">Heures</th><th className="cell-num">% h</th>
                      </tr></thead>
                      <tbody>
                        {visibles.map((l) => (
                          <tr key={`${l.mesureId}-${l.publicType}`}>
                            <td className="cell-prestation"><span className="nom-prestation" style={{ whiteSpace: 'normal' }}>{l.nom}</span></td>
                            <td><span className="stat-pill" style={{ background: `color-mix(in srgb, ${PUBLIC_COULEUR[l.publicType] ?? 'var(--s-autre)'} 22%, transparent)` }}>{l.publicType}</span></td>
                            <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.mandantNom ?? ''}>{l.mandantNom ?? '—'}</td>
                            <td className="cell-num">{fmtInt(l.participants)}</td>
                            <td className="cell-num">{fmtInt(l.presences)}</td>
                            <td className="cell-num">{l.tauxAbsenteisme != null ? fmtPct(l.tauxAbsenteisme) : '—'}</td>
                            <td className="cell-num tnum">{fmtHeures(l.heures)}</td>
                            <td className="cell-num">{fmtPct(l.pctHeures)}</td>
                          </tr>
                        ))}
                        {visibles.length === 0 && <tr><td colSpan={8} className="text-center text-ink-3 p-16">Aucune mesure sur ce périmètre.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <p className="an-note">Chaque présence est rattachée à la mesure active du participant :
                    <code>présence → mandat → mesure → mandant → famille</code>. Le % d'heures est rapporté au
                    total du périmètre filtré ; dispensé·es hors du taux d'absentéisme. Une mesure servie à
                    plusieurs publics (ex. AI en CTT) apparaît une ligne par public.</p>
                </div>
              </div>
            );
          })()}

          {/* ══ ESPACE 4 ══ */}
          {tab === 'mod' && (
            <div className="an-grid">
              <div className="an-card an-col-12">
                <header className="an-card-h"><h3>Le fait &amp; ses dimensions</h3><span className="an-sub">schéma en étoile · eveil-datamodel</span></header>
                <p className="an-note" style={{ marginTop: 0 }}>Un seul <b>fait</b> — la ligne de <code>presence</code> — porte tous les rapports ; chaque ancien classeur Excel n'en est qu'une <em>coupe filtrée</em>. La prestation est classée sur <b>deux axes</b> : catégorie × pôle.</p>
                <div className="an-star">
                  <div className="an-dim an-fact"><h4>◆ presence (fait)</h4><p>1 <code>inscription</code> × 1 <code>seance</code>. Statut ∈ present · absent_excuse · absent_injustifie. Heures calculées (arrivée→départ), jamais stockées.</p></div>
                  <div className="an-dim"><h4>prestation</h4><p>le gabarit (ex-« atelier »). <code>seance</code> = son instance datée.</p></div>
                  <div className="an-dim"><h4>catégorie · axe 1</h4><p>Atelier · Espace · Suivi individuel</p></div>
                  <div className="an-dim"><h4>pôle · axe 2</h4><p>Corporel · Thérapeutique · Créatif · Écologie &amp; nature · Transition · Théorique · Pratique de travail · Vers l'emploi · Sortie · Évolution</p></div>
                  <div className="an-dim"><h4>public → mandant</h4><p><code>mandate → funder → famille</code> : AI · RI-CCC · RI-CTT · SISP · Autres</p></div>
                  <div className="an-dim"><h4>mesure</h4><p>le produit vendu à un mandant (nom commercial, base légale, prix).</p></div>
                  <div className="an-dim"><h4>lieu / salle</h4><p><code>seance → salle → lieu</code> (occupation des espaces).</p></div>
                  <div className="an-dim"><h4>mesures dérivées</h4><p>absentéisme · occupation · usage · pic hebdo · mixité · heures perdues.</p></div>
                </div>
              </div>
              <div className="an-card an-col-12">
                <header className="an-card-h"><h3>Des 6 classeurs aux 3 espaces</h3></header>
                <p className="an-note" style={{ marginTop: 0 }}>L'app reproduit les anciens rapports Excel comme des <b>vues filtrées</b> d'un même fait — au lieu d'un fichier figé par combinaison public × catégorie.</p>
                <div className="table-scroll">
                  <table className="table table-compact">
                    <thead><tr><th>Classeur Excel</th><th>Devient dans l'app</th><th>Coupe (eveil-datamodel)</th></tr></thead>
                    <tbody>
                      <tr><td>Participants_AI_Activites_(Pro/NON-Pro)</td><td>Participants &amp; activités</td><td>public = AI</td></tr>
                      <tr><td>Participants_RI_Activites_(Pro/NON-Pro)</td><td>Participants &amp; activités</td><td>public = RI-CCC / RI-CTT</td></tr>
                      <tr><td>Indicateurs_utilisation_espaces</td><td>Espaces &amp; occupation</td><td>agrégation par <code>lieu / salle</code></td></tr>
                      <tr><td>Données brutes participation</td><td>(source) le fait</td><td>la table <code>presence</code></td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
